import axios from "axios";
import { getTokenSync, setToken, clearToken, isHydrated } from "./tokenStorage";

// iter v31 r4 — Backend URL detection robusta per WebView Capacitor.
//
// CONTESTO: dentro l'iOS Capacitor app, `window.location.origin` può ritornare
// `capacitor://localhost` (scheme interno della WebView) anziché
// `https://prontoimmo.it`. Se usiamo direttamente l'origin, tutte le chiamate
// API vanno a `capacitor://localhost/api/...` → ERR_FAILED, l'app sembra rotta
// (foto profilo "?", template non caricati, paywall in "Sincronizzazione…").
//
// FIX: se l'origin è uno scheme nativo Capacitor/Ionic, usiamo l'URL di
// produzione esplicito. Su web (prontoimmo.it o preview) restiamo
// sull'origin corrente per supportare il custom domain deploy.
const BUILD_TIME_BACKEND = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_BACKEND = "https://prontoimmo.it";
const NATIVE_SCHEMES = ["capacitor://", "ionic://", "file://"];

function detectBackendUrl() {
  if (typeof window === "undefined") return BUILD_TIME_BACKEND;
  const origin = window.location?.origin || "";
  if (!origin) return BUILD_TIME_BACKEND || PRODUCTION_BACKEND;
  // WebView nativa: forza l'URL di produzione (le API non vivono sullo
  // scheme capacitor:// che è solo per i file dell'app).
  if (NATIVE_SCHEMES.some((s) => origin.startsWith(s))) {
    return PRODUCTION_BACKEND;
  }
  return origin;
}

export const BACKEND_URL = detectBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // send cookies for Google session
  // iter v31 r12 — Timeout di default alzato a 60s. Gli endpoint LLM
  // (extract entita' agenda, classifier intent) impiegano regolarmente
  // 5-15s in produzione. Il vecchio limite di 15s causava timeout
  // visibili dall'utente come toast rosso "timeout of 15000ms exceeded"
  // sul flow Assistente vocale.
  timeout: 60000,
});

api.interceptors.request.use((cfg) => {
  // iter v30 r130 — il token vive ora in tokenStorage (memoria + cookie +
  // localStorage + Capacitor Preferences su iOS). getTokenSync() ritorna
  // dalla memoria in O(1), con fallback automatico a cookie/localStorage
  // se l'hydration non è ancora completata (race condition al boot).
  const token = getTokenSync();
  if (token) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

// Wrapper di compatibilità — il vecchio resto del codice importa
// `saveJwt` / `clearJwt` da qui. Internamente delegano a tokenStorage che
// gestisce anche la persistenza nativa Capacitor Preferences.
export const saveJwt = (token) => setToken(token);
export const clearJwt = () => clearToken();

// iter v32 r3 — Strategia anti-logout-spurio: l'interceptor NON deautentica
// più al primo (o secondo) 401. Invece, chiama /auth/me come check
// autoritativo. Solo se /auth/me conferma il 401, allora pulisce e
// redirige. Altrimenti il 401 era un errore transitorio (race con resume
// iOS, glitch di rete, backend hiccup, edge proxy che incolla 401 sul
// timeout) e l'utente resta dentro l'app.
//
// CONTESTO: l'utente lamentava "stavo dentro l'app facendo cose e mi
// sbatteva fuori". Causa: 2x 401 in 3s erano sufficienti a triggerare il
// redirect, anche se il token era ancora valido. Ora un singolo redirect
// avviene SOLO dopo che /auth/me ha confermato l'invalidità.
let _validating = null;
let _redirecting = false;

async function _validateOrLogout() {
  if (_validating) return _validating;
  _validating = (async () => {
    try {
      await api.get("/auth/me", { _skipValidate: true });
      return true; // token ancora valido — non logout
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        // Token davvero invalido secondo il backend: logout pulito
        _redirecting = true;
        try {
          clearJwt();
          if (typeof window !== "undefined" && !/\/(login|register|auth-callback)/.test(window.location.pathname)) {
            try { sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search); } catch { /* no-op */ }
            window.location.href = "/login?expired=1";
          }
        } catch { /* no-op */ }
        return false;
      }
      // /auth/me ha fallito per altro motivo (network, 5xx): NON deautenticare
      return true;
    } finally {
      // Reset dopo 8s: più 401 vicini nel tempo riusano lo stesso check
      setTimeout(() => { _validating = null; }, 8000);
    }
  })();
  return _validating;
}

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Skip su endpoint auth (evita loop) e su chiamate marker _skipValidate
    const isAuthCheckUrl = /\/auth\/(me|login|register|session|logout)/.test(url);
    const skipValidate = error?.config?._skipValidate === true;

    if (status === 401 && !isAuthCheckUrl && !skipValidate && !_redirecting && isHydrated()) {
      // Validazione autoritativa via /auth/me, in background.
      // Il chiamante riceve comunque l'errore originale (che può gestire
      // con un toast); il redirect avviene SOLO se /auth/me conferma il 401.
      _validateOrLogout();
    }
    // iter 275 round 36: 402 Payment Required → crediti finiti.
    // Dispatch evento globale per CreditExhaustedModal (ascoltato da App.js).
    if (status === 402 && typeof window !== "undefined") {
      const detail = error?.response?.data?.detail || {};
      try {
        window.dispatchEvent(new CustomEvent("pronto:credits-exhausted", {
          detail: {
            needed: detail.needed,
            available: detail.available,
            message: detail.message || "Crediti esauriti",
          },
        }));
      } catch { /* no-op */ }
    }
    // iter 275 round 50: 429 anti-abuso (burst o cap mensile hard).
    if (status === 429 && typeof window !== "undefined") {
      const detail = error?.response?.data?.detail;
      // slowapi ritorna detail come stringa "Rate limit exceeded: ..."
      // il nostro abuse_guard ritorna un object {error, message, ...}.
      const msg = typeof detail === "object" && detail?.message
        ? detail.message
        : "Troppe richieste in poco tempo. Aspetta qualche minuto e riprova.";
      try {
        window.dispatchEvent(new CustomEvent("pronto:rate-limited", {
          detail: { message: msg, raw: detail },
        }));
      } catch { /* no-op */ }
    }
    // iter 313 r14: 503 con error="global_budget_exceeded" → la beta ha
    // superato il budget mensile totale. Toast informativo all'utente.
    if (status === 503 && typeof window !== "undefined") {
      const detail = error?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "global_budget_exceeded") {
        try {
          window.dispatchEvent(new CustomEvent("pronto:global-budget-exceeded", {
            detail: {
              message: detail.message,
              // iter 313 r15: NON passiamo importi monetari al frontend.
            },
          }));
        } catch { /* no-op */ }
      }
    }
    // iter v31 r18 — Diagnostica 500 prod iPhone. Un toast da qualche parte
    // mostra "Request failed with status code 500" ma non riusciamo a
    // capire da quale endpoint. Qui logghiamo in console + window.
    // Visibile via Safari Web Inspector connesso al device.
    if (status === 500 && typeof window !== "undefined") {
      const errInfo = {
        url,
        method: error?.config?.method,
        status: 500,
        detail: error?.response?.data?.detail || error?.response?.data || null,
        ts: new Date().toISOString(),
      };
      try {
        console.error("[Pronto 500]", errInfo);
        window.__pronto_last_500 = errInfo;
        // Anche dispatch evento globale così possiamo prendere via listener.
        window.dispatchEvent(new CustomEvent("pronto:server-error-500", {
          detail: errInfo,
        }));
      } catch { /* no-op */ }
    }
    return Promise.reject(error);
  },
);

/** Extract a user-friendly error message from any axios error.
 *  Handles: string detail, FastAPI 422 array detail, dict detail with
 *  `message`/`msg`/`error` (es. crediti insufficienti, rate limit), network errors. */
export const apiError = (err, fallback = "Si è verificato un errore") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || "Errore di validazione").filter(Boolean).join(" · ") || fallback;
  }
  if (detail && typeof detail === "object") {
    // iter v26 — supporto dict {message, error, msg, needed, available, ...}
    // ritornati da backend per crediti, abuse guard, rate limit. Prima il
    // chiamante passava l'oggetto a toast.error → React crash.
    if (detail.message) return String(detail.message);
    if (detail.msg) return String(detail.msg);
    if (detail.error && typeof detail.error === "string") {
      if (detail.needed != null && detail.available != null) {
        return `Crediti insufficienti · servono ${detail.needed}, disponibili ${detail.available}`;
      }
      return detail.error;
    }
  }
  // Fallback su status code comuni
  const status = err?.response?.status;
  if (status === 402) return "Crediti esauriti · aggiorna il piano in Profilo";
  if (status === 404) return "Risorsa non trovata";
  if (status === 429) return "Troppe richieste · riprova tra poco";
  return err?.message || fallback;
};


// iter v33 r14 · User M1492: «l'ho messa in background e mi ha dato Network
// Error, ma poi si è aggiornato».
// Root cause: quando iOS sospende l'app in background, le fetch pendenti
// falliscono con `err.message === "Network Error"` (axios) o `code:
// "ERR_NETWORK"`. Al ritorno in foreground le retry vanno a buon fine, ma
// il toast rosso resta visibile → l'agente pensa che sia rotto.
//
// Fix: tracciamo l'ultimo momento in cui l'app è stata nascosta e
// forniamo un helper `notifyError(err, fallback)` che sopprime il toast se
// (a) è un network error e (b) l'app è correntemente in background o lo
// è stata negli ultimi 5s.
let _lastHiddenAt = 0;
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      _lastHiddenAt = Date.now();
    }
  });
}

export const isBackgroundNetworkError = (err) => {
  const isNet = (
    err?.message === "Network Error"
    || err?.code === "ERR_NETWORK"
    || err?.code === "ECONNABORTED"
    || (typeof navigator !== "undefined" && navigator.onLine === false)
  );
  if (!isNet) return false;
  const nowHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
  const recentlyHidden = _lastHiddenAt > 0 && (Date.now() - _lastHiddenAt) < 5000;
  return nowHidden || recentlyHidden;
};

/** Wrapper attorno a toast.error(apiError(...)) che sopprime silenziosamente
 *  gli errori di rete generati mentre l'app è in background su iOS.
 *  Passalo così: notifyError(err, "Fallback human message").
 *  Se vuoi personalizzare le opzioni di sonner: notifyError(err, fallback, { duration: 6000 }).
 */
export const notifyError = (err, fallback = "Si è verificato un errore", opts = {}) => {
  // Import "lazy" per evitare cycle
  if (isBackgroundNetworkError(err)) {
    // eslint-disable-next-line no-console
    console.warn("[notifyError] soppresso · network error mentre app in background", err);
    return;
  }
  // Import di toast fatto qui per evitare dipendenza circolare al top
  // eslint-disable-next-line global-require
  const { toast } = require("sonner");
  toast.error(apiError(err, fallback), opts);
};
